// Import necessary modules/components
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DeleteModal from "../../components/Common/DeleteModal";
import * as Yup from "yup";
import { useFormik } from "formik";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import axios from "axios"; import { handleAuthError } from "../../utils";
import withAuth from "../withAuth";
import CountrySelect from "../CountrySelect";
import { baseUrl } from '../../Network';
import Select from "react-select";
import levelOpt from "../../constants/levelOpt.json";
import {
    Col,
    Container,
    Row,
    Modal,
    ModalHeader,
    ModalBody,
    Tooltip,
    Form,
    Label,
    Input,
    FormFeedback,
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PaginationControls from "../../components/Common/PaginationControls";
import ReferralUsageModal from "./ReferralUsageModal";

const AI_REFERRAL_SIGNUP_BASE = "https://ai.rydlearning.com/auth/sign-up";

const ManageCoupon = () => {
    const isReferralMode =
        window.location.pathname.includes("manage-referral-code") ||
        window.location.pathname.includes("ai/referral-codes");
    const entityName = isReferralMode ? "Referral Code" : "Coupon";
    const entityNamePlural = isReferralMode ? "Referral Codes" : "Coupons";
    const [coupons, setCoupons] = useState([]);
    const [coupon, setCoupon] = useState({});
    const [modal, setModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [usageModal, setUsageModal] = useState(false);
    const [usageLoading, setUsageLoading] = useState(false);
    const [usageError, setUsageError] = useState(null);
    const [usageData, setUsageData] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [editTooltipOpen, setEditTooltipOpen] = useState(false);
    const [deleteTooltipOpen, setDeleteTooltipOpen] = useState(false);
    const [statusTooltipOpen, setStatusTooltipOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loading2, setLoading2] = useState(false);
    const [levelOptions, levelOptionsChange] = useState([]);
    const [aiPlans, setAiPlans] = useState([]);
    const [applyDiscountToAllPlans, setApplyDiscountToAllPlans] = useState(true);
    const [planDiscounts, setPlanDiscounts] = useState({});
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 20,
        hasNextPage: false,
        hasPreviousPage: false
    });
    const [showAll, setShowAll] = useState(false);

    const buildReferralLink = (code) =>
        `${AI_REFERRAL_SIGNUP_BASE}?ref=${encodeURIComponent(String(code || "").trim())}`;

    const copyReferralLink = async (code) => {
        const link = buildReferralLink(code);
        try {
            await navigator.clipboard.writeText(link);
            toast.success("Referral link copied");
        } catch {
            toast.error("Could not copy link");
        }
    };

    const openUsageModal = async (c) => {
        setUsageModal(true);
        setUsageLoading(true);
        setUsageError(null);
        setUsageData(null);
        try {
            const res = await axios.get(`${baseUrl}/admin/coupon/${c.id}/usage`);
            if (res?.data?.status && res?.data?.data) {
                setUsageData(res.data.data);
            } else {
                setUsageError(res?.data?.message || "Unable to load usage details");
            }
        } catch (e) {
            handleAuthError(e);
            setUsageError(e?.response?.data?.message || "Unable to load usage details");
        } finally {
            setUsageLoading(false);
        }
    };

    const closeUsageModal = () => {
        setUsageModal(false);
        setUsageError(null);
        setUsageData(null);
    };

    const formatReferralDiscountSummary = (c) => {
        const applyAll =
            c.applyDiscountToAllPlans !== false &&
            c.applyDiscountToAllPlans !== 0 &&
            c.applyDiscountToAllPlans !== "0" &&
            c.applyDiscountToAllPlans !== "false";

        let planMap = c.planDiscounts;
        if (typeof planMap === "string") {
            try {
                planMap = JSON.parse(planMap);
            } catch {
                planMap = null;
            }
        }

        if (!applyAll && planMap && typeof planMap === "object" && !Array.isArray(planMap)) {
            const entries = Object.entries(planMap).filter(([, d]) => Number(d?.value) > 0);
            if (entries.length) {
                return entries
                    .map(([key, d]) => `${key}: ${d.isPercentage ? `${d.value}%` : d.value}`)
                    .join(" · ");
            }
        }

        if (Number(c.value) > 0) {
            return c.isPercentage
                ? `${Number(c.value).toFixed(1)}%${applyAll ? " (all plans)" : ""}`
                : `${Number(c.value).toFixed(1)}${applyAll ? " (all plans)" : ""}`;
        }
        return "—";
    };

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            code: coupon.code || "",
            agentName: coupon.agentName || "",
            discount: coupon.value || "",
            isPercentage: coupon.isPercentage || false,
            byCountry: coupon.byCountry || "",
            accessDays: coupon.accessDays === 0 || coupon.accessDays ? coupon.accessDays : "",
        },
        validationSchema: Yup.object({
            code: Yup.string()
                .required(`Please Enter ${entityName} Code`)
                .max(isReferralMode ? 32 : 6, isReferralMode ? "Code cannot be more than 32 characters" : "Code cannot be more than 6 characters"),
            agentName: isReferralMode
                ? Yup.string().required("Please Enter Agent Name").max(255, "Agent name is too long")
                : Yup.string().max(255, "Agent name is too long"),
            discount: isReferralMode
                ? Yup.number()
                : Yup.number().required("Please Enter Discount"),
            isPercentage: isReferralMode
                ? Yup.boolean()
                : Yup.boolean().required("Please Choose Discount Type"),
            byCountry: isReferralMode
                ? Yup.string()
                : Yup.string().required("Please Choose Country"),
            accessDays: isReferralMode
                ? Yup.number().min(0).max(366).nullable()
                : Yup.mixed().nullable(),
        }),

        onSubmit: async (values) => {
            try {
                setLoading2(true);
                const discountType =
                    values.isPercentage === true || values.isPercentage === "true";
                const accessDays = isReferralMode ? Number(values.accessDays) || 0 : 0;

                if (isReferralMode && applyDiscountToAllPlans) {
                    const hasDiscount = values.discount && Number(values.discount) > 0;
                    if (!hasDiscount && accessDays <= 0) {
                        setLoading2(false);
                        return toast.error(
                            "Enter a discount greater than zero and/or access days for free trial activation"
                        );
                    }
                }

                if (isReferralMode && !applyDiscountToAllPlans) {
                    const hasAny = Object.values(planDiscounts).some((d) => Number(d?.value) > 0);
                    if (!hasAny && accessDays <= 0) {
                        setLoading2(false);
                        return toast.error(
                            "Add a plan discount and/or access days for free trial activation"
                        );
                    }
                }

                const normalizedPlanDiscounts = Object.fromEntries(
                    Object.entries(planDiscounts).map(([key, d]) => [
                        key,
                        {
                            value: Number(d?.value) || 0,
                            isPercentage: d?.isPercentage !== false,
                        },
                    ])
                );

                const newCoupon = isReferralMode
                    ? {
                        code: values.code,
                        agentName: values.agentName || "",
                        value: applyDiscountToAllPlans
                            ? Number(values.discount) || 0
                            : Number(Object.values(normalizedPlanDiscounts).find((d) => Number(d?.value) > 0)?.value || 0),
                        isPercentage: applyDiscountToAllPlans
                            ? discountType
                            : !!Object.values(normalizedPlanDiscounts).find((d) => Number(d?.value) > 0)?.isPercentage,
                        applyDiscountToAllPlans,
                        planDiscounts: applyDiscountToAllPlans ? null : normalizedPlanDiscounts,
                        accessDays,
                        byCountry: "All",
                        isActive: true,
                        isReferral: true,
                        byLevel: 0,
                        mLevel: [0],
                    }
                    : {
                        code: values.code,
                        value: values.discount,
                        isPercentage: discountType,
                        byCountry: values.byCountry,
                        isActive: true,
                        isReferral: false,
                    };

                let response;
                if (isEdit) {
                    response = await axios.put(
                        `${baseUrl}/admin/coupon/edit/${coupon.id}`,
                        isReferralMode ? newCoupon : { ...newCoupon, ...getLevelFilter() }
                    );
                    toast.success(`${entityName} updated successfully`);
                    toggle();
                    setLoading2(false);
                    return;
                } else {
                    if (!isReferralMode && levelOptions.length === 0) {
                        setLoading2(false);
                        return toast.error("Please select a level option");
                    }
                    response = await axios.post(
                        `${baseUrl}/admin/coupon/create`,
                        isReferralMode ? newCoupon : { ...newCoupon, ...getLevelFilter() },
                    );
                    toast.success(`${entityName} created successfully`);
                    setLoading2(false);
                }
 
                const responseData = response.data;

                if (isEdit) {
                    const updatedCoupons = coupons.map((c) =>
                        c.id === coupon.id ? { ...c, ...responseData } : c
                    );
                    setCoupons(updatedCoupons);
                } else {
                    setCoupons([...coupons, responseData]);
                }
                toggle();
            } catch (error) { handleAuthError(error)
                console.error("Error:", error);
                toast.error(error.response?.data?.message || `Failed to save ${entityName.toLowerCase()}`);
                setLoading2(false);
            }
        },
    });

    //filter levels
    const getLevelFilter = () => {
        const lF = levelOptions.map((opt) => opt.value)
        if (lF.length === 1) return {
            byLevel: lF[0],
            mLevel: lF
        }
        return { byLevel: 0, mLevel: lF };
    }

    useEffect(() => {
        fetchCoupons();
    }, [modal, currentPage, pageSize, searchQuery]);

    useEffect(() => {
        if (!isReferralMode) return;
        const loadAiPlans = async () => {
            try {
                const res = await axios.get(`${baseUrl}/admin/subscription/plans`);
                const rows = Array.isArray(res.data?.data) ? res.data.data : [];
                const visible = rows
                    .filter((p) => p.isVisible !== false)
                    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
                setAiPlans(visible);
                setPlanDiscounts((prev) => {
                    const next = { ...prev };
                    visible.forEach((p) => {
                        const key = p.planKey;
                        if (!next[key]) {
                            next[key] = { value: "", isPercentage: true };
                        }
                    });
                    return next;
                });
            } catch (error) {
                handleAuthError(error);
                console.error("Error loading AI plans:", error);
            }
        };
        void loadAiPlans();
    }, [isReferralMode]);

    const fetchCoupons = async () => {
        try {
            setLoading(true);
            const limit = showAll ? 10000 : pageSize;
            const params = {
                page: currentPage,
                limit: limit,
                type: isReferralMode ? "referral" : "coupon",
                ...(searchQuery && { search: searchQuery })
            };
            const response = await axios.get(`${baseUrl}/admin/coupon/all`, { params });
            const responseData = response.data.data;
            const data = Array.isArray(responseData) ? responseData : (responseData?.data || []);
            const paginationData = responseData?.pagination || {
                currentPage: 1,
                totalPages: 1,
                totalItems: Array.isArray(data) ? data.length : 0,
                itemsPerPage: limit,
                hasNextPage: false,
                hasPreviousPage: false
            };
            setCoupons(Array.isArray(data) ? data : []);
            setPagination(paginationData);
            setLoading(false);
        } catch (error) { handleAuthError(error)
            setLoading(false);
            console.error("Error:", error);
            toast.error("Failed to load coupons. List may be outdated.");
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (size) => {
        if (size === 'all') {
            setShowAll(true);
            setPageSize(10000);
        } else {
            setShowAll(false);
            setPageSize(parseInt(size));
            setCurrentPage(1);
        }
    };

    const goToNextPage = () => {
        if (pagination.hasNextPage) {
            setCurrentPage(currentPage + 1);
        }
    };

    const goToPreviousPage = () => {
        if (pagination.hasPreviousPage) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
    };

    const toggle = () => {
        if (modal) {
            setApplyDiscountToAllPlans(true);
            setPlanDiscounts({});
        }
        setModal(!modal);
    };

    const handleCouponClick = (couponData) => {
        setCoupon(couponData);
        setIsEdit(true);
        if (isReferralMode) {
            const applyAll = couponData.applyDiscountToAllPlans !== false;
            setApplyDiscountToAllPlans(applyAll);
            const existing = couponData.planDiscounts || {};
            const next = {};
            (aiPlans.length ? aiPlans : Object.keys(existing).map((planKey) => ({ planKey, name: planKey }))).forEach((p) => {
                const key = p.planKey;
                next[key] = {
                    value: existing[key]?.value ?? "",
                    isPercentage: existing[key]?.isPercentage !== false,
                };
            });
            setPlanDiscounts(next);
        }
        toggle();
    };

    const openCreateModal = () => {
        setIsEdit(false);
        setCoupon({});
        setApplyDiscountToAllPlans(true);
        const next = {};
        aiPlans.forEach((p) => {
            next[p.planKey] = { value: "", isPercentage: true };
        });
        setPlanDiscounts(next);
        toggle();
    };

    const onClickDelete = (couponData) => {
        setCoupon(couponData);
        setDeleteModal(true);
    };

    const handleDeleteCoupon = async () => {
        try {
            await axios.delete(`${baseUrl}/admin/coupon/${coupon.id}`);
            const updatedCoupons = coupons.filter((c) => c.id !== coupon.id);
            setCoupons(updatedCoupons);
            setDeleteModal(false);
            toast.success("Coupon deleted successfully");
        } catch (error) { handleAuthError(error)
            console.error("Error:", error);
            toast.error("Failed to delete coupon");
        }
    };

    const toggleCouponStatus = async (couponId, isActive) => {
        try {

            await axios.put(`${baseUrl}/admin/coupon/edit/${couponId}`, { isActive: !isActive });
            const updatedCoupons = coupons.map((c) =>
                c.id === couponId ? { ...c, isActive: !isActive } : c
            );
            setCoupons(updatedCoupons);
            toast.success(`Coupon ${!isActive ? 'enabled' : 'disabled'} successfully`);
        } catch (error) { handleAuthError(error)
            console.error("Error:", error);
            toast.error("Failed to toggle coupon status");
        }
    };

    const toggleRevoke = async (couponId) => {
        try {
            await axios.put(`${baseUrl}/admin/coupon/revoke/${couponId}`);
            toast.success(`Coupon revoked successfully`);
            fetchCoupons();
        } catch (error) { handleAuthError(error)
            console.error("Error:", error);
            toast.error("Failed to toggle coupon revoke");
        }
    };



    const countUniqueProgramUsers = (coupon) => {
        if (!coupon?.programs || !Array.isArray(coupon.programs)) {
            return 0;
        }

        const uniqueUserIds = new Set(
            coupon.programs
                .filter(program => program?.childId)
                .map(program => program.childId)
        );

        return uniqueUserIds.size;
    };

    return (
        <React.Fragment>
            <DeleteModal
                show={deleteModal}
                onDeleteClick={handleDeleteCoupon}
                onCloseClick={() => setDeleteModal(false)}
            />
            {isReferralMode && (
                <ReferralUsageModal
                    isOpen={usageModal}
                    toggle={closeUsageModal}
                    loading={usageLoading}
                    error={usageError}
                    usage={usageData}
                />
            )}
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Dashboard" breadcrumbItem={`Manage ${entityNamePlural}`} />
                    <Row>
                        <Col lg="12">
                            <Row className="align-items-center">
                                <Col md={6}>
                                    <div className="mb-3">
                                        <h5 className="card-title">
                                            {entityName} List{" "}
                                            <span className="text-muted fw-normal ms-2">
                                                ({coupons.length})
                                            </span>
                                        </h5>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 mb-3">
                                        <div>
                                            <Input
                                                type="text"
                                                placeholder="Search"
                                                value={searchQuery}
                                                onChange={handleSearch}
                                            />
                                        </div>
                                        <div>
                                            <button
                                                className="btn btn-light"
                                                onClick={openCreateModal}>
                                                <i className="bx bx-plus me-1"></i> Add New {entityName}
                                            </button>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col xl="12" style={{ overflow: 'scroll', width: '98%' }}>
                                    {loading ? (
                                        <div className="text-center mt-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </div>
                                    ) : Array.isArray(coupons) && coupons.length === 0 ? (
                                        <div className="text-center mt-5">
                                            <h3>No data available</h3>
                                        </div>
                                    ) : (
                                        <table className="table align-middle  table-hover">
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Code</th>
                                                    <th>Access key</th>
                                                    {isReferralMode && <th>Agent</th>}
                                                    {isReferralMode && <th>Access days</th>}
                                                    {isReferralMode && <th>Discount</th>}
                                                    {!isReferralMode && <th>Discount</th>}
                                                    {!isReferralMode && <th>Usage</th>}
                                                    {isReferralMode && <th>Usages</th>}
                                                    {isReferralMode && <th>Paid</th>}
                                                    {!isReferralMode && <th>Total Unique</th>}
                                                    {!isReferralMode && <th>Country</th>}
                                                    {!isReferralMode && <th>Level</th>}
                                                    {!isReferralMode && <th>mLevel</th>}
                                                    <th>Status</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {coupons.map((c, index) => {
                                                    const globalIndex = showAll 
                                                        ? index 
                                                        : (pagination.currentPage - 1) * pagination.itemsPerPage + index;
                                                    return (
                                                        <tr key={c.id || index}>
                                                            <td>{globalIndex + 1}</td>
                                                        <td>{c.code}</td>
                                                        <td>{c.accessKey ? c.accessKey : "---"}</td>
                                                        {isReferralMode && <td>{c.agentName || "---"}</td>}
                                                        {isReferralMode && (
                                                            <td>
                                                                {Number(c.accessDays) > 0
                                                                    ? `${Number(c.accessDays)} day${Number(c.accessDays) === 1 ? "" : "s"}`
                                                                    : "—"}
                                                            </td>
                                                        )}
                                                        {isReferralMode && (
                                                            <td style={{ maxWidth: 280, whiteSpace: "normal" }}>
                                                                {formatReferralDiscountSummary(c)}
                                                            </td>
                                                        )}
                                                        {!isReferralMode && (
                                                            <td>
                                                                {c.isPercentage
                                                                    ? `${Number(c.value).toFixed(1)}%`
                                                                    : `${Number(c.value).toFixed(1)}%`}
                                                            </td>
                                                        )}
                                                        {!isReferralMode && <td>{c?.programs?.length}</td>}
                                                        {isReferralMode && <td>{c?.referralSignups || 0}</td>}
                                                        {isReferralMode && <td>{c?.referralPaid || 0}</td>}
                                                        {!isReferralMode && <td>{countUniqueProgramUsers(c)}</td>}
                                                        {!isReferralMode && (
                                                            <td>
                                                                {c?.byCountry?.split(",")?.length > 1 ? c?.byCountry.includes("Nigeria") ? "Only Diaspora" : "Only Int'l" : c?.byCountry}
                                                            </td>
                                                        )}
                                                        {!isReferralMode && (
                                                            <td>
                                                                {c.byLevel === 0 ? "All Levels" : c.byLevel}
                                                            </td>
                                                        )}
                                                        {!isReferralMode && (
                                                            <td>
                                                                {Array.isArray(c?.mLevel) && c.mLevel.length > 1
                                                                    ? c.mLevel.join(", ")
                                                                    : "Single Level"}
                                                            </td>
                                                        )}
                                                        <td>{c.isActive ? "Active" : "Inactive"}</td>
                                                        <td>
                                                            <div className="d-flex gap-3 align-items-center">
                                                                {isReferralMode && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-primary"
                                                                        title="View usage"
                                                                        onClick={() => void openUsageModal(c)}
                                                                    >
                                                                        View usage
                                                                    </button>
                                                                )}
                                                                {isReferralMode && (
                                                                    <Link
                                                                        className="text-info"
                                                                        to="#"
                                                                        title="Copy referral link"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            void copyReferralLink(c.code);
                                                                        }}
                                                                    >
                                                                        <i className="mdi mdi-content-copy font-size-18"></i>
                                                                    </Link>
                                                                )}
                                                                <Link
                                                                    className="text-success"
                                                                    to="#"
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        handleCouponClick(c);
                                                                    }}
                                                                >
                                                                    <i className="mdi mdi-pencil font-size-18"></i>
                                                                </Link>

                                                                <Tooltip
                                                                    isOpen={deleteTooltipOpen}
                                                                    target="delete"
                                                                    toggle={() => setDeleteTooltipOpen(!deleteTooltipOpen)}
                                                                    placement="top"
                                                                    delay={{ show: 100, hide: 100 }}
                                                                >
                                                                    Delete
                                                                </Tooltip>
                                                                <Link
                                                                    className="text-danger"
                                                                    to="#"
                                                                    id="delete"
                                                                    onMouseEnter={() => setDeleteTooltipOpen(true)}
                                                                    onMouseLeave={() => setDeleteTooltipOpen(false)}
                                                                    onClick={() => onClickDelete(c)}
                                                                >
                                                                    <i className="mdi mdi-delete font-size-18"></i>
                                                                </Link>

                                                                <Tooltip
                                                                    isOpen={statusTooltipOpen}
                                                                    target="status"
                                                                    toggle={() => setStatusTooltipOpen(!statusTooltipOpen)}
                                                                    placement="top"
                                                                    delay={{ show: 100, hide: 100 }}
                                                                >
                                                                    {c.isActive ? 'Deactivate' : 'Activate'}
                                                                </Tooltip>
                                                                <Link
                                                                    className="text-secondary"
                                                                    onClick={() => toggleCouponStatus(c.id, c.isActive)}
                                                                    id="status"
                                                                    onMouseEnter={() => setStatusTooltipOpen(true)}
                                                                    onMouseLeave={() => setStatusTooltipOpen(false)}
                                                                >
                                                                    {c.isActive ? (
                                                                        <i className="mdi mdi-sync font-size-18"></i>
                                                                    ) : (
                                                                        <i className="mdi mdi-sync-off font-size-18"></i>
                                                                    )}
                                                                </Link>
                                                                <Link
                                                                    className="text-secondary"
                                                                    onClick={() => toggleRevoke(c.id)}
                                                                    id="revoke"
                                                                >
                                                                    <button className="btn btn-primary">Access_key</button>
                                                                </Link>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    )}
                                    {coupons.length > 0 && (
                                        <PaginationControls
                                            currentPage={pagination.currentPage}
                                            totalPages={pagination.totalPages}
                                            totalItems={pagination.totalItems}
                                            pageSize={pagination.itemsPerPage}
                                            showAll={showAll}
                                            handlePageChange={handlePageChange}
                                            handlePageSizeChange={handlePageSizeChange}
                                            goToNextPage={goToNextPage}
                                            goToPreviousPage={goToPreviousPage}
                                            canPreviousPage={pagination.hasPreviousPage}
                                            canNextPage={pagination.hasNextPage}
                                            paginatedDataLength={coupons.length}
                                        />
                                    )}
                                    <Modal isOpen={modal} toggle={toggle}>
                                        <ModalHeader toggle={toggle}>
                                            {isEdit ? `Edit ${entityName}` : `Add New ${entityName}`}
                                        </ModalHeader>
                                        <ModalBody>
                                            <Form onSubmit={validation.handleSubmit}>
                                                <Row>
                                                    <Col xs={12}>
                                                        <div className="mb-3">
                                                            <Label className="form-label">Code</Label>
                                                            <Input
                                                                name="code"
                                                                type="text"
                                                                placeholder="Code"
                                                                onChange={validation.handleChange}
                                                                onBlur={validation.handleBlur}
                                                                value={validation.values.code || ""}
                                                                invalid={
                                                                    validation.touched.code &&
                                                                    validation.errors.code
                                                                }
                                                            />
                                                            <FormFeedback type="invalid">
                                                                {validation.errors.code}
                                                            </FormFeedback>
                                                        </div>
                                                        {isReferralMode && (
                                                            <div className="mb-3">
                                                                <Label className="form-label">Agent Name</Label>
                                                                <Input
                                                                    name="agentName"
                                                                    type="text"
                                                                    placeholder="Agent name"
                                                                    onChange={validation.handleChange}
                                                                    onBlur={validation.handleBlur}
                                                                    value={validation.values.agentName || ""}
                                                                    invalid={
                                                                        validation.touched.agentName &&
                                                                        validation.errors.agentName
                                                                    }
                                                                />
                                                                <FormFeedback type="invalid">
                                                                    {validation.errors.agentName}
                                                                </FormFeedback>
                                                            </div>
                                                        )}
                                                        {isReferralMode && (
                                                            <>
                                                                <div className="mb-3">
                                                                    <Label className="form-label">AI access days (free trial)</Label>
                                                                    <Input
                                                                        name="accessDays"
                                                                        type="select"
                                                                        onChange={validation.handleChange}
                                                                        onBlur={validation.handleBlur}
                                                                        value={
                                                                            validation.values.accessDays === 0 ||
                                                                            validation.values.accessDays
                                                                                ? validation.values.accessDays
                                                                                : ""
                                                                        }
                                                                    >
                                                                        <option value="">No auto-activation</option>
                                                                        <option value={7}>7 days</option>
                                                                        <option value={14}>14 days</option>
                                                                        <option value={30}>30 days</option>
                                                                        <option value={60}>60 days</option>
                                                                        <option value={90}>90 days</option>
                                                                    </Input>
                                                                    <small className="text-muted d-block mt-1">
                                                                        When a parent registers with this code, AI access is activated for this many days.
                                                                    </small>
                                                                </div>
                                                                <div className="mb-3">
                                                                    <Label className="form-label">Discount scope</Label>
                                                                    <Input
                                                                        type="select"
                                                                        value={applyDiscountToAllPlans ? "all" : "per"}
                                                                        onChange={(e) => setApplyDiscountToAllPlans(e.target.value === "all")}
                                                                    >
                                                                        <option value="all">Apply same discount to all plans</option>
                                                                        <option value="per">Set discount per plan</option>
                                                                    </Input>
                                                                </div>
                                                                {applyDiscountToAllPlans ? (
                                                                    <>
                                                                        <div className="mb-3">
                                                                            <Label className="form-label">Referral Discount (optional if access days set)</Label>
                                                                            <Input
                                                                                name="discount"
                                                                                type="number"
                                                                                placeholder="Discount (0 allowed with access days)"
                                                                                onChange={validation.handleChange}
                                                                                onBlur={validation.handleBlur}
                                                                                value={validation.values.discount || ""}
                                                                                invalid={
                                                                                    validation.touched.discount &&
                                                                                    validation.errors.discount
                                                                                }
                                                                            />
                                                                            <FormFeedback type="invalid">
                                                                                {validation.errors.discount}
                                                                            </FormFeedback>
                                                                        </div>
                                                                        <div className="mb-3">
                                                                            <Label className="form-label">Discount Type</Label>
                                                                            <Input
                                                                                type="select"
                                                                                name="isPercentage"
                                                                                onChange={validation.handleChange}
                                                                                onBlur={validation.handleBlur}
                                                                                value={
                                                                                    validation.values.isPercentage === true ||
                                                                                    validation.values.isPercentage === "true"
                                                                                        ? "true"
                                                                                        : "false"
                                                                                }
                                                                            >
                                                                                <option value="false">Direct Discount</option>
                                                                                <option value="true">Percentage</option>
                                                                            </Input>
                                                                        </div>
                                                                    </>
                                                                ) : (
                                                                    <div className="mb-3">
                                                                        <Label className="form-label">Plan discounts</Label>
                                                                        {!aiPlans.length ? (
                                                                            <p className="text-muted small mb-0">No AI subscription plans found.</p>
                                                                        ) : (
                                                                            aiPlans.map((plan) => {
                                                                                const key = plan.planKey;
                                                                                const row = planDiscounts[key] || { value: "", isPercentage: true };
                                                                                return (
                                                                                    <div key={key} className="border rounded p-2 mb-2">
                                                                                        <div className="fw-semibold mb-2">
                                                                                            {plan.name} <span className="text-muted">({key})</span>
                                                                                        </div>
                                                                                        <div className="d-flex gap-2">
                                                                                            <Input
                                                                                                type="number"
                                                                                                placeholder="Discount"
                                                                                                value={row.value}
                                                                                                onChange={(e) =>
                                                                                                    setPlanDiscounts((prev) => ({
                                                                                                        ...prev,
                                                                                                        [key]: { ...row, value: e.target.value },
                                                                                                    }))
                                                                                                }
                                                                                            />
                                                                                            <Input
                                                                                                type="select"
                                                                                                value={row.isPercentage ? "true" : "false"}
                                                                                                onChange={(e) =>
                                                                                                    setPlanDiscounts((prev) => ({
                                                                                                        ...prev,
                                                                                                        [key]: {
                                                                                                            ...row,
                                                                                                            isPercentage: e.target.value === "true",
                                                                                                        },
                                                                                                    }))
                                                                                                }
                                                                                            >
                                                                                                <option value="false">Direct</option>
                                                                                                <option value="true">%</option>
                                                                                            </Input>
                                                                                        </div>
                                                                                    </div>
                                                                                );
                                                                            })
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                        {!isReferralMode && (
                                                            <>
                                                                <div className="mb-3">
                                                                    <Label className="form-label">Discount</Label>
                                                                    <Input
                                                                        name="discount"
                                                                        type="number"
                                                                        placeholder="Discount"
                                                                        onChange={validation.handleChange}
                                                                        onBlur={validation.handleBlur}
                                                                        value={validation.values.discount || ""}
                                                                        invalid={
                                                                            validation.touched.discount &&
                                                                            validation.errors.discount
                                                                        }
                                                                    />
                                                                    <FormFeedback type="invalid">
                                                                        {validation.errors.discount}
                                                                    </FormFeedback>
                                                                </div>
                                                                <div className="mb-3">
                                                                    <Label className="form-label">Discount Type</Label>
                                                                    <Input
                                                                        type="select"
                                                                        name="isPercentage"
                                                                        onChange={validation.handleChange}
                                                                        onBlur={validation.handleBlur}
                                                                        value={validation.values.isPercentage || ""}
                                                                        invalid={
                                                                            validation.touched.isPercentage &&
                                                                            validation.errors.isPercentage
                                                                        }
                                                                    >
                                                                        <option value="">Select Discount Type</option>
                                                                        <option value={false}>Direct Discount</option>
                                                                        <option value={true}>Percentage</option>
                                                                    </Input>
                                                                    <FormFeedback type="invalid">
                                                                        {validation.errors.isPercentage}
                                                                    </FormFeedback>
                                                                </div>
                                                                <div className="mb-3">
                                                                    <Label className="form-label">Country</Label>
                                                                    <CountrySelect
                                                                        type="text"
                                                                        name="byCountry"
                                                                        onChange={validation.handleChange}
                                                                        onBlur={validation.handleBlur}
                                                                        value={validation.values.byCountry || ""}
                                                                        invalid={
                                                                            validation.touched.byCountry &&
                                                                            validation.errors.byCountry
                                                                        }
                                                                    />
                                                                    <FormFeedback type="invalid">
                                                                        {validation.errors.byCountry}
                                                                    </FormFeedback>
                                                                </div>
                                                                <div className="mb-3">
                                                                    <Label className="form-label">Level</Label>
                                                                    <Select isMulti options={levelOpt} type="select"
                                                                        name="byLevel"
                                                                        onChange={levelOptionsChange}
                                                                        value={levelOptions}
                                                                        invalid={
                                                                            validation.touched.byLevel &&
                                                                            validation.errors.byLevel
                                                                        } />
                                                                    <FormFeedback type="invalid">
                                                                        {validation.errors.byLevel}
                                                                    </FormFeedback>
                                                                </div>
                                                            </>
                                                        )}
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col>
                                                        <div className="text-end">
                                                            {!isEdit ? (
                                                                <button
                                                                    disabled={loading2}
                                                                    type="submit"
                                                                    className="btn btn-primary save-user">
                                                                    {loading2 ? 'Creating...' : `Create ${entityName}`}
                                                                </button>
                                                            ) : (
                                                                <button
                                                                    disabled={loading2}
                                                                    type="submit"
                                                                    className="btn btn-primary save-user">
                                                                    {loading2 ? 'Updating...' : `Update ${entityName}`}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </Form>
                                        </ModalBody>
                                    </Modal>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Container>
            </div>
            <ToastContainer />
        </React.Fragment>
    );
};

export default withAuth(ManageCoupon);
